import { useCallback, useState } from 'react'
import { Platform } from 'react-native'
import { api } from '@api'
import { useLanguage } from '@context'
import { shareFile, showSuccessToast, textToBase64 } from '@utils'
import { useApiErrorHandler } from './useApiErrorHandler'

export type EventsExportFormat = 'json' | 'csv'

const FILENAME: Record<EventsExportFormat, string> = {
  json: 'keeptouch-mes-donnees-evenements.json',
  csv: 'keeptouch-mes-donnees-evenements.csv',
}

const MIME: Record<EventsExportFormat, string> = {
  json: 'application/json;charset=utf-8',
  csv: 'text/csv;charset=utf-8',
}

/**
 * "Mes données d'événements" export (owner decision 27/09/2026): free for
 * every account, unlike the Premium contacts export it sits next to in
 * Settings. Fetches `GET /me/events/export` (JSON or `?format=csv`), then
 * delivers it the same way as `useExportContacts`:
 *   - web    → triggers a file download in the browser
 *   - native → opens the OS share sheet with the file itself
 */
export const useExportEventsData = () => {
  const { t } = useLanguage()
  const handleError = useApiErrorHandler()
  const [exportingFormat, setExportingFormat] = useState<EventsExportFormat | null>(null)

  const exportEventsData = useCallback(
    async (format: EventsExportFormat) => {
      setExportingFormat(format)
      try {
        const { data } = await api.get<string>(`/me/events/export?format=${format}`, {
          responseType: 'text',
          // The body is raw JSON or CSV text, not something axios should re-parse.
          transformResponse: (raw) => raw,
        })
        const content = typeof data === 'string' ? data : String(data)
        const filename = FILENAME[format]

        if (Platform.OS === 'web') {
          const blob = new Blob([content], { type: MIME[format] })
          const url = URL.createObjectURL(blob)
          const anchor = document.createElement('a')
          anchor.href = url
          anchor.download = filename
          document.body.appendChild(anchor)
          anchor.click()
          anchor.remove()
          URL.revokeObjectURL(url)
        } else {
          // A real file (Android's Share API alone would only send the text).
          await shareFile({ fileName: filename, base64: textToBase64(content), title: filename })
        }

        showSuccessToast(t('eventsExport.successTitle'), t('eventsExport.successMessage'))
      } catch (error) {
        handleError(error)
      } finally {
        setExportingFormat(null)
      }
    },
    [t, handleError],
  )

  return { exportEventsData, exportingFormat }
}
